import type { ElementSchemaType, FONT, SchemaKeys } from '@vedalib/editor/lib/brand'
import type {
  EditorElement,
  ElementSchemaDeviceType,
  StyleValue,
} from '@vedalib/editor/lib/elements'
import * as R from 'ramda'
import { useEffect, useMemo } from 'react'
import { useContextSelector } from 'use-context-selector'

import { chainName } from 'components/formKit'
import type { IFieldConfig } from 'components/formKit/types'
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/commonConstans'
import { DeviceMode } from 'services/Store/Project/enums'
import type { MapType } from 'services/Store/Project/types'

import { BrandContext } from './BrandContext'
import type {
  BrandType,
  ElementFeedbackCss,
  ElementFontCss,
  ElementFontValue,
  ElementStyleCss,
  ElementStyleValue,
  ExtractFont,
  ExtractStyle,
  FieldsSchema,
} from './types'
import { mergeWithDeviceMode } from './utils'
import {
  collectCssVars,
  merge,
  replaceImageIdToPath,
  unwrapStyleSchema,
  validateCss,
} from './utils'

export const useUpdateSchema = <S extends FieldsSchema>(
  path: string[] = EMPTY_ARRAY,
  configs: Partial<IFieldConfig> = EMPTY_OBJECT,
  config: S,
): S =>
  useMemo(() => {
    return R.mapObjIndexed((tag, tagName) => {
      return R.mapObjIndexed((field, fieldName) => {
        const config = [configs, field].reduce((acc, cur) => ({
          ...acc,
          ...cur,
          disabled: acc.disabled || cur?.disabled,
          hidden: acc.hidden || cur?.hidden,
          readOnly: acc.readOnly || cur?.readOnly,
          props: { ...acc.props, ...(cur?.props ?? {}) },
          useConfigs: [...(acc.useConfigs ?? []), ...(cur?.useConfigs ?? [])],
        }))
        return chainName(...path, tagName, fieldName, config as IFieldConfig)
      }, tag)
    }, config) as S
  }, [config, configs, path])

export const useBrandVars = (brand: BrandType) => {
  useEffect(() => {
    const themeCss = brand.theme
    const style = document.documentElement.style
    R.forEachObjIndexed(
      (value, key) => style.setProperty(`--brand-${String(key)}`, String(validateCss(value, key))),
      themeCss,
    )
    return () => {
      R.forEachObjIndexed((_value, key) => style.removeProperty(`--brand-${String(key)}`), themeCss)
    }
  }, [brand.theme])
}

export const useBrandTheme = () => useContextSelector(BrandContext, (brand) => brand.theme)

export function useBrandElementSchema<S extends SchemaKeys>(type: S): BrandType['schema'][S]
export function useBrandElementSchema<S extends SchemaKeys>(type?: S): BrandType['schema'][S] | null
export function useBrandElementSchema<S extends SchemaKeys>(type?: S) {
  return useContextSelector(BrandContext, (context) => (type ? context.schema[type] : null))
}

export const useElementStyleValues = <S extends SchemaKeys>(
  type: S,
  device: DeviceMode,
  style: ElementSchemaDeviceType = {},
) => {
  const styleSchema = useBrandElementSchema(type).style

  return useMemo(() => {
    const brand = unwrapStyleSchema(styleSchema)
    const final = R.mergeDeepRight(brand, style)
    return mergeWithDeviceMode(final, device)
  }, [styleSchema, style, device]) as ElementStyleValue<ExtractStyle<S>>
}

export const useElementDefaultValues = <S extends SchemaKeys>(element: EditorElement) => {
  const { type, style, font, feedback } = element
  const styleSchema = useContextSelector(BrandContext, (context) => context.schema[type].style)
  const fontBrand = useContextSelector(BrandContext, (context) => context.font)
  const fontSchema = useContextSelector(BrandContext, (context) => context.schema[type].font)
  const feedbackBrand = useContextSelector(BrandContext, (context) => context.feedback)
  const feedbackSchema = useContextSelector(
    BrandContext,
    (context) => context.schema[type].feedback,
  )

  return useMemo(() => {
    const brandStyle = unwrapStyleSchema(styleSchema)
    const finalStyle = R.mergeDeepRight(brandStyle, style)

    const brandFont = unwrapStyleSchema(merge(fontBrand, fontSchema as typeof fontBrand))
    const baseFont = merge(
      R.mapObjIndexed(R.prop('base'), brandFont),
      R.mapObjIndexed(R.prop('base'), font),
    )
    const finalFont = merge(brandFont, font)
    const fontWithBase = R.mapObjIndexed(
      (obj, key) => R.mapObjIndexed((font) => merge(baseFont[key], font), obj),
      finalFont,
    )

    const brandFeedback = unwrapStyleSchema(
      merge(feedbackBrand, feedbackSchema as typeof feedbackBrand),
    )
    const finalFeedback = R.mergeDeepRight(brandFeedback, feedback)

    return {
      ...element,
      style: {
        [DeviceMode.desktop]: mergeWithDeviceMode(finalStyle, DeviceMode.desktop),
        [DeviceMode.tablet]: mergeWithDeviceMode(finalStyle, DeviceMode.tablet),
        [DeviceMode.mobile]: mergeWithDeviceMode(finalStyle, DeviceMode.mobile),
      },
      font: {
        [DeviceMode.desktop]: R.pick(
          R.keys(fontSchema.desktop),
          mergeWithDeviceMode(merge(finalFont, fontWithBase), DeviceMode.desktop),
        ),
        [DeviceMode.tablet]: R.pick(
          R.keys(fontSchema.desktop),
          mergeWithDeviceMode(merge(finalFont, fontWithBase), DeviceMode.tablet),
        ),
        [DeviceMode.mobile]: R.pick(
          R.keys(fontSchema.desktop),
          mergeWithDeviceMode(merge(finalFont, fontWithBase), DeviceMode.mobile),
        ),
      },
      feedback: {
        [DeviceMode.desktop]: mergeWithDeviceMode(finalFeedback, DeviceMode.desktop),
        [DeviceMode.tablet]: mergeWithDeviceMode(finalFeedback, DeviceMode.tablet),
        [DeviceMode.mobile]: mergeWithDeviceMode(finalFeedback, DeviceMode.mobile),
      },
    }
  }, [
    fontBrand,
    styleSchema,
    fontSchema,
    feedbackSchema,
    font,
    style,
    feedback,
    feedbackBrand,
    element,
  ]) as ElementStyleValue<ExtractStyle<S>>
}

export const useElementStyleCss = <S extends SchemaKeys>(
  type: S,
  device: DeviceMode,
  style: ElementSchemaDeviceType = {},
) => {
  const values = useElementStyleValues(type, device, style) as MapType<Record<string, StyleValue>>

  return useMemo(() => {
    replaceImageIdToPath(values)
    values.root = { ...values.root, ...collectCssVars(values) }
    return R.mapObjIndexed(R.mapObjIndexed(validateCss), values)
  }, [values]) as ElementStyleCss<ExtractStyle<S>>
}

export const useElementFontValues = <S extends SchemaKeys>(
  type: S,
  device: DeviceMode,
  font: ElementSchemaDeviceType = {},
) => {
  const fontBrand = useContextSelector(BrandContext, (context) => context.font)
  const fontSchema = useContextSelector(BrandContext, (context) => context.schema[type].font)

  return useMemo(() => {
    const brand = unwrapStyleSchema(merge(fontBrand, fontSchema as typeof fontBrand))
    const base = merge(
      R.mapObjIndexed(R.prop('base'), brand),
      R.mapObjIndexed(R.prop('base'), font),
    )

    const final = merge(brand, font)
    const withBase = R.mapObjIndexed(
      (obj, key) => R.mapObjIndexed((font) => merge(base[key], font), obj),
      final,
    )
    return R.pick(R.keys(fontSchema.desktop), mergeWithDeviceMode(merge(final, withBase), device))
  }, [fontSchema, fontBrand, font, device]) as ElementFontValue<ExtractFont<S>>
}

export const useElementFontCss = <S extends SchemaKeys>(
  type: S,
  device: DeviceMode,
  font: ElementSchemaDeviceType = {},
) => {
  const values = useElementFontValues(type, device, font) as MapType<Record<string, StyleValue>>

  return useMemo(
    () => R.mapObjIndexed(R.mapObjIndexed(validateCss), values),
    [values],
  ) as ElementFontCss<ExtractFont<S>>
}

const useFontValues = (device?: DeviceMode) => {
  const brand = useContextSelector(BrandContext, (context) => unwrapStyleSchema(context.font))
  return useMemo(() => {
    const base = R.mapObjIndexed(R.prop('base'), brand)
    const withBase = R.mapObjIndexed(
      (obj, key) => R.mapObjIndexed((font) => merge(base[key], font), obj),
      brand,
    )

    return device ? mergeWithDeviceMode(merge(brand, withBase), device) : merge(brand, withBase)
  }, [brand, device]) as ElementFontValue
}

export const useFontCss = (device: DeviceMode) => {
  const values = useFontValues(device) as MapType<Record<string, StyleValue>>
  return useMemo(() => {
    return R.mapObjIndexed(R.mapObjIndexed(validateCss), values)
  }, [values]) as ElementFontCss<typeof FONT.desktop>
}

export const useElementFeedbackValues = <S extends SchemaKeys>(
  type: S,
  device: DeviceMode,
  feedback: ElementSchemaDeviceType = {},
) => {
  const feedbackSchema = useContextSelector(
    BrandContext,
    (context) => context.schema[type].feedback,
  )
  const feedbackBrand = useContextSelector(BrandContext, (context) => context.feedback)

  return useMemo(() => {
    const brand = unwrapStyleSchema(merge(feedbackBrand, feedbackSchema as typeof feedbackBrand))
    const final = R.mergeDeepRight(brand, feedback)
    return mergeWithDeviceMode(final, device)
  }, [feedbackSchema, feedbackBrand, feedback, device]) as ElementStyleValue<ExtractStyle<S>>
}

export const useElementFeedbackCss = <S extends SchemaKeys>(
  type: S,
  device: DeviceMode,
  feedback: ElementSchemaDeviceType = {},
) => {
  const values = useElementFeedbackValues(type, device, feedback) as MapType<
    Record<string, StyleValue>
  >
  return useMemo(() => {
    replaceImageIdToPath(values)

    return R.mapObjIndexed(R.mapObjIndexed(validateCss), values)
  }, [values]) as ElementFeedbackCss<ElementSchemaType>
}

export const useFeedbackValues = (device: DeviceMode) => {
  const feedbackSchema = useContextSelector(BrandContext, (context) => context.feedback)

  return useMemo(() => {
    const brand = unwrapStyleSchema(feedbackSchema)
    return mergeWithDeviceMode(brand, device)
  }, [feedbackSchema, device]) as ElementStyleValue
}

export const useFeedbackCss = (device: DeviceMode) => {
  const values = useFeedbackValues(device) as MapType<Record<string, StyleValue>>

  return useMemo(() => {
    replaceImageIdToPath(values)
    values.root = { ...values.root, ...collectCssVars(values) }
    return R.mapObjIndexed(R.mapObjIndexed(validateCss), values)
  }, [values]) as ElementStyleCss
}
